/**
 * The views bar: one pill per saved layout of the circle tree.
 *
 * A view is "how I like to look at this" — the mode and visibility of every
 * circle, plus where the camera was (docs/CIRCLES.md §6). Switching is not an
 * edit: the rings tween to the new arrangement and, if the view remembered a
 * camera, the camera goes back with them.
 *
 * **A view is a snapshot, not a recording.** Rearranging the circles changes
 * the working layout and the ring, and nothing else; the pill of the view you
 * started from grows a dot, and Save and Revert appear next to it. Before this,
 * every mode change debounced itself into the selected view and the default
 * everyone opens on drifted under its owner. Moving the camera is looking, not
 * arranging: it lights no dot, and Save simply records where you were.
 *
 * It sits on the footer bar rather than inside a panel, because switching views
 * is the most frequent thing anyone does with circles and it should not cost a
 * panel. Owners get the star, the rename and the delete; a viewer gets the
 * pills, and everything they do stays in their browser.
 */
import type { View, ViewCamera, ViewLayout } from '@circle/shared';
import { ApiError, api } from '../app/api';
import { button, el, setChildren, textField, withBusy } from './dom';
import { openDialog } from './dialog';
import { toast, toastError } from './toast';
import { circleErrorMessage } from './circles';

export interface ViewsBarOptions {
  calendarId: string;
  /** Read live: a reload replaces the page's array wholesale. */
  views: () => View[];
  /** Which one is showing. */
  current: () => string | null;
  /** Owners save, rename, star and delete. Viewers only switch. */
  editable: boolean;
  /** Apply this view — layout, and the camera if it kept one. */
  onSelect: (view: View) => void;
  /** What "Save as…" records; the camera only if the dialog's toggle is left on. */
  capture: () => { layout: ViewLayout; camera: ViewCamera };
  /** Refetch the bundle so the pills and the star are the server's. */
  reload: () => Promise<void>;
  /** A new view was made or the current one went away. */
  onCurrentChange?: (viewId: string | null) => void;
  /** Whether the working layout differs from what the current view holds. */
  dirty?: () => boolean;
  /** Write the working layout and the camera into the current view. */
  onSave?: () => Promise<void>;
  /** Put the current view's own arrangement back. */
  onRevert?: () => void;
}

export class ViewsBar {
  readonly root: HTMLElement;
  private readonly options: ViewsBarOptions;

  constructor(options: ViewsBarOptions) {
    this.options = options;
    this.root = el('div', { class: 'views', role: 'group', 'aria-label': 'Views' });
    this.paint();
  }

  /** Unsaved work on the view that is showing. Only an owner can have any. */
  private get isDirty(): boolean {
    return this.options.editable && Boolean(this.options.dirty?.());
  }

  paint(): void {
    const views = this.options.views();
    const current = this.options.current();
    setChildren(this.root, [
      ...views.map((view) => this.pill(view, view.id === current)),
      views.length === 0
        ? el('span', { class: 'small muted', text: 'No saved views yet.' })
        : null,
      ...(this.isDirty ? this.unsavedControls() : []),
      this.options.editable
        ? button('Save as…', () => this.openSaveDialog(), {
            class: 'btn--small btn--quiet',
            'data-role': 'save-view',
          })
        : null,
    ]);
  }

  /**
   * Save and Revert, next to the pill they belong to.
   *
   * They are on the bar rather than inside the pill because they are the two
   * things you look for once you have changed something, and a pill is a
   * target you press to switch — not a place to hide a second verb.
   */
  private unsavedControls(): HTMLElement[] {
    const save = button(
      'Save',
      () =>
        void withBusy(save, async () => {
          await this.options.onSave?.();
          this.paint();
        }),
      { class: 'btn--small btn--primary', 'data-role': 'save-layout' },
    );
    const revert = button(
      'Revert',
      () => {
        this.options.onRevert?.();
        this.paint();
      },
      { class: 'btn--small btn--quiet', 'data-role': 'revert-layout' },
    );
    return [save, revert];
  }

  destroy(): void {
    this.root.remove();
  }

  private pill(view: View, active: boolean): HTMLElement {
    const unsaved = active && this.isDirty;
    const group = el('div', {
      class: 'view-pill',
      'data-view': view.id,
      'data-active': active ? 'true' : 'false',
      'data-default': view.isDefault ? 'true' : 'false',
      'data-dirty': unsaved ? 'true' : 'false',
    });
    const pick = el('button', {
      class: 'view-pill__pick',
      type: 'button',
      'aria-pressed': active ? 'true' : 'false',
      onClick: () => this.options.onSelect(view),
    });
    if (unsaved) {
      // A dot, not an asterisk in the name: the name is what you read to find
      // the view, and it should not change shape when you touch something.
      pick.append(
        el('span', { class: 'view-pill__dot', 'aria-hidden': 'true', 'data-role': 'dirty' }),
        el('span', { text: view.name }),
        el('span', { class: 'sr-only', text: ' — unsaved changes' }),
      );
    } else {
      pick.textContent = view.name;
    }
    group.appendChild(pick);
    if (this.options.editable) {
      group.appendChild(this.star(view));
      // Rename and delete belong to the view you are actually looking at.
      if (active) group.appendChild(this.editButton(view));
    }
    return group;
  }

  /**
   * The default is what a viewer opening the share link is given, and what this
   * page opens on with no `?view=`. Exactly one carries it, so the star is a
   * radio in all but name.
   */
  private star(view: View): HTMLElement {
    const btn = el('button', {
      class: 'view-pill__star',
      type: 'button',
      'data-role': 'default',
      'aria-pressed': view.isDefault ? 'true' : 'false',
      'aria-label': view.isDefault ? `${view.name} is the default view` : `Make ${view.name} the default`,
      title: view.isDefault ? 'The default view' : 'Make this the default',
      text: view.isDefault ? '★' : '☆',
    });
    btn.addEventListener('click', () => {
      if (view.isDefault) return;
      void withBusy(btn, async () => {
        try {
          await api.setDefaultView(this.options.calendarId, view.id);
          await this.options.reload();
          this.paint();
          toast(`${view.name} is the view everyone opens on.`);
        } catch (err) {
          toastError(err, 'That view did not become the default.');
        }
      });
    });
    return btn;
  }

  private editButton(view: View): HTMLElement {
    return el('button', {
      class: 'view-pill__edit',
      type: 'button',
      'data-role': 'edit-view',
      'aria-label': `Rename or delete ${view.name}`,
      title: 'Rename or delete',
      text: '⋯',
      onClick: () => this.openEditDialog(view),
    });
  }

  // --- dialogs --------------------------------------------------------------

  private openSaveDialog(): void {
    const { layout, camera } = this.options.capture();
    const name = textField({
      label: 'Name',
      name: 'view-name',
      placeholder: 'Just the family',
      required: true,
      maxlength: '60',
    });
    // On by default: most people want to come back to what they are looking
    // at. Off, the view opens wherever the camera happens to be, and a fit of
    // its own rings when there is nothing in the hash.
    const keepCamera = el('input', { type: 'checkbox', name: 'view-camera', checked: true });
    const handle = openDialog({
      title: 'Save this arrangement',
      description: 'It keeps how every circle is drawn right now. You can come back to it from the bar.',
      body: [
        name.root,
        el(
          'label',
          { class: 'row small muted' },
          keepCamera,
          el('span', { text: 'Also remember this viewpoint — the zoom and the spin as they are now' }),
        ),
      ],
    });
    const cancel = button('Cancel', () => handle.close());
    const save = button(
      'Save view',
      () =>
        void withBusy(save, async () => {
          const value = name.input.value.trim();
          if (!value) {
            name.setError('A name, however short.');
            return;
          }
          try {
            const created = await api.createView(this.options.calendarId, {
              name: value,
              layout,
              ...(keepCamera.checked ? { camera } : {}),
            });
            handle.close();
            await this.options.reload();
            this.options.onCurrentChange?.(created.id);
            this.paint();
            toast(`Saved ${created.name}.`);
          } catch (err) {
            name.setError(null);
            toastError(err, 'That view was not saved.');
          }
        }),
      { class: 'btn--primary' },
    );
    handle.foot.append(el('span', { class: 'grow' }), cancel, save);
    handle.root.appendChild(handle.foot);
    name.input.focus();
  }

  private openEditDialog(view: View): void {
    const name = textField({
      label: 'Name',
      name: 'view-name',
      value: view.name,
      required: true,
      maxlength: '60',
    });
    const handle = openDialog({ title: `View: ${view.name}`, body: [name.root] });

    const remove = button(
      'Delete view',
      () =>
        void withBusy(remove, async () => {
          try {
            await api.deleteView(this.options.calendarId, view.id);
            handle.close();
            await this.options.reload();
            const left = this.options.views();
            this.options.onCurrentChange?.(left.find((v) => v.isDefault)?.id ?? left[0]?.id ?? null);
            this.paint();
            toast(`Deleted ${view.name}.`);
          } catch (err) {
            // `VIEW_LAST` is the one refusal worth its own sentence: a calendar
            // always keeps somewhere to be looked at from.
            const code = err instanceof ApiError ? err.code : undefined;
            const known = code === 'VIEW_LAST' ? circleErrorMessage(code) : undefined;
            if (known) toast(known, 'error', 7000);
            else toastError(err, 'That view was not deleted.');
          }
        }),
      { class: 'btn--small btn--danger' },
    );
    const cancel = button('Cancel', () => handle.close());
    const save = button(
      'Save changes',
      () =>
        void withBusy(save, async () => {
          const value = name.input.value.trim();
          if (!value) {
            name.setError('A name, however short.');
            return;
          }
          try {
            await api.patchView(this.options.calendarId, view.id, { name: value });
            handle.close();
            await this.options.reload();
            this.paint();
            toast('View renamed.');
          } catch (err) {
            toastError(err, 'That name did not save.');
          }
        }),
      { class: 'btn--primary' },
    );
    handle.foot.append(remove, el('span', { class: 'grow' }), cancel, save);
    handle.root.appendChild(handle.foot);
    name.input.focus();
  }
}
